import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Button,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Switch,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Image as ImageIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import { useAuth } from "../context/auth";
import PermissionGuard from "./PermissionGuard";
import ConfirmDialog from "./ConfirmDialog";

const GalleryManagement = () => {
  const [images, setImages] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({ open: false, imageId: null, imageTitle: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);
  const [newImageFile, setNewImageFile] = useState(null);
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();

  const baseUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    let isMounted = true;
    const source = axios.CancelToken.source();

    const fetchGalleryImages = async () => {
      try {
        setLoading(true);
        setError("");

        if (!isAuthenticated || !token) {
          setError("Please login to manage gallery");
          setLoading(false);
          return;
        }

        const response = await axios.get(`${baseUrl}/auth/gallery`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          cancelToken: source.token,
        });

        if (isMounted) {
          setImages(response.data);
        }
      } catch (error) {
        if (isMounted && !axios.isCancel(error)) {
          console.error("Error fetching gallery images:", error);
          if (error.response?.status === 401) {
            setError("Authentication failed. Please login again.");
          } else {
            setError("Failed to load gallery images. Please try again.");
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchGalleryImages();

    return () => {
      isMounted = false;
      source.cancel("Component unmounted. Request canceled.");
    };
  }, [baseUrl, token, isAuthenticated]);

  const handleAdd = () => navigate("/dashboard/addGallery");

  const handleStartEdit = (image) => {
    setEditingId(image.id);
    setEditedData({
      title: image.title || '',
      description: image.description || '',
      displayOrder: image.displayOrder || 0,
      isActive: image.isActive !== undefined ? image.isActive : true
    });
    setNewImageFile(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditedData({});
    setNewImageFile(null);
    setError("");
  };

  const handleInputChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setError('Please select a valid image file (JPG, PNG, GIF, or WEBP)');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      setNewImageFile(file);
      setError("");
    }
  };

  const handleSaveEdit = async () => {
    if (!editedData.title || editedData.title.trim() === '') {
      setError('Title is required');
      return;
    }

    setSaveLoading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append('title', editedData.title);
      formData.append('description', editedData.description || '');
      formData.append('displayOrder', editedData.displayOrder);
      formData.append('isActive', editedData.isActive);

      if (newImageFile) {
        formData.append('image', newImageFile);
      }

      const response = await axios.put(
        `${baseUrl}/auth/gallery/${editingId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Update local state with the response
      setImages(images.map(img =>
        img.id === editingId ? { ...img, ...response.data } : img
      ));

      setSuccess('Gallery image updated successfully!');
      handleCancelEdit();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Error updating gallery image:", error);
      setError(error.response?.data?.error || "Failed to update image. Please try again.");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleOpenDialog = (id) => {
    const image = images.find(img => img.id === id);
    if (!image) return;

    setDeleteDialog({
      open: true,
      imageId: id,
      imageTitle: image.title
    });
  };

  const handleCloseDialog = () => {
    setDeleteDialog({ open: false, imageId: null, imageTitle: '' });
    setDeleteLoading(false);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(`${baseUrl}/auth/gallery/${deleteDialog.imageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setImages(images.filter((img) => img.id !== deleteDialog.imageId));
      handleCloseDialog();
    } catch (error) {
      console.error("Error deleting gallery image:", error);
      setError(error.response?.data?.error || "Failed to delete image. Please try again.");
      setDeleteLoading(false);
    }
  };

  const filteredImages = images.filter((image) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      image.title?.toLowerCase().includes(searchLower) ||
      image.description?.toLowerCase().includes(searchLower)
    );
  });

  const truncateText = (text, maxLength, mobileLength = 30) => {
    if (!text) return "N/A";
    // Use shorter length on mobile devices
    const isMobile = window.innerWidth < 600;
    const length = isMobile ? mobileLength : maxLength;
    if (text.length <= length) return text;
    return text.substring(0, length) + "...";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (!isAuthenticated) {
    return (
      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
        <Typography variant="h4" component="h1" sx={{ mb: 3, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
          Gallery Management
        </Typography>
        <Alert severity="error" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          Please login to manage gallery.
        </Alert>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/login")}
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          Go to Login
        </Button>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
        <Typography variant="h4" component="h1" sx={{ mb: 3, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
          Gallery Management
        </Typography>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CircularProgress size={{ xs: 40, sm: 50, md: 60 }} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: { xs: 2, sm: 3 }, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
          Gallery Management
        </Typography>
        <PermissionGuard resource="gallery" action="create">
          <Button variant="contained" onClick={handleAdd} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            Add Gallery Image
          </Button>
        </PermissionGuard>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Success Alert */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <TextField
        fullWidth
        label="Search gallery images..."
        variant="outlined"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 3,
          '& .MuiInputBase-root': {
            fontSize: { xs: '0.875rem', sm: '1rem' }
          },
          '& .MuiInputLabel-root': {
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }
        }}
      />

      {filteredImages.length === 0 && !loading ? (
        <Alert severity="info" sx={{
          mb: 3,
          fontSize: { xs: '0.875rem', sm: '1rem' },
          '& .MuiAlert-icon': {
            fontSize: { xs: '1.2rem', sm: '1.5rem' }
          }
        }}>
          {images.length === 0
            ? "No gallery images found. Add your first image to get started."
            : "No images match your search criteria."}
        </Alert>
      ) : (
        <TableContainer component={Paper} sx={{
          overflowX: 'auto',
          boxShadow: { xs: 1, sm: 2, md: 3 }
        }}>
          <Table sx={{ minWidth: { xs: 500, sm: 650, md: 'auto' } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#1976d2' }}>
                <TableCell sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
                  whiteSpace: 'nowrap',
                  padding: { xs: '8px', sm: '16px' }
                }}>Image</TableCell>
                <TableCell sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
                  whiteSpace: 'nowrap',
                  padding: { xs: '8px', sm: '16px' }
                }}>Title</TableCell>
                <TableCell sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
                  whiteSpace: 'nowrap',
                  padding: { xs: '8px', sm: '16px' }
                }}>Description</TableCell>
                <TableCell sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
                  whiteSpace: 'nowrap',
                  padding: { xs: '8px', sm: '16px' }
                }}>Display Order</TableCell>
                <TableCell sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
                  whiteSpace: 'nowrap',
                  padding: { xs: '8px', sm: '16px' }
                }}>Status</TableCell>
                <TableCell sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
                  whiteSpace: 'nowrap',
                  padding: { xs: '8px', sm: '16px' }
                }}>Date Added</TableCell>
                <TableCell sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
                  whiteSpace: 'nowrap',
                  padding: { xs: '4px', sm: '16px' }
                }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredImages.map((image) => {
                const isEditing = editingId === image.id;
                return (
                  <TableRow key={image.id} sx={{ backgroundColor: isEditing ? '#f5f5f5' : 'inherit' }}>
                    {/* Image Column */}
                    <TableCell sx={{ padding: { xs: '8px', sm: '16px' } }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                        {image.imageUrl ? (
                          <Box
                            component="img"
                            src={newImageFile && isEditing ? URL.createObjectURL(newImageFile) : `${baseUrl}${image.imageUrl}`}
                            alt={image.title}
                            sx={{
                              width: { xs: '60px', sm: '70px', md: '80px' },
                              height: { xs: '45px', sm: '52px', md: '60px' },
                              objectFit: 'cover',
                              borderRadius: '4px',
                              display: 'block'
                            }}
                            onError={(e) => {
                              e.target.src = "https://via.placeholder.com/80x60/4B5563/FFFFFF?text=Image";
                            }}
                          />
                        ) : (
                          <ImageIcon sx={{ fontSize: { xs: 30, sm: 35, md: 40 }, color: '#bbb' }} />
                        )}
                        {isEditing && (
                          <Button
                            component="label"
                            size="small"
                            startIcon={<CloudUploadIcon />}
                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                          >
                            Upload
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={handleImageChange}
                            />
                          </Button>
                        )}
                      </Box>
                    </TableCell>

                    {/* Title Column */}
                    <TableCell sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      maxWidth: { xs: '100px', sm: '150px', md: '200px' },
                      padding: { xs: '8px', sm: '16px' }
                    }}>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={editedData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontSize: { xs: '0.75rem', sm: '0.875rem' }
                            }
                          }}
                        />
                      ) : (
                        <Box sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {image.title || "N/A"}
                        </Box>
                      )}
                    </TableCell>

                    {/* Description Column */}
                    <TableCell sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      maxWidth: { xs: '120px', sm: '200px', md: '300px' },
                      padding: { xs: '8px', sm: '16px' }
                    }}>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          rows={2}
                          value={editedData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontSize: { xs: '0.75rem', sm: '0.875rem' }
                            }
                          }}
                        />
                      ) : (
                        truncateText(image.description, 60)
                      )}
                    </TableCell>

                    {/* Display Order Column */}
                    <TableCell sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      padding: { xs: '8px', sm: '16px' }
                    }}>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={editedData.displayOrder}
                          onChange={(e) => handleInputChange('displayOrder', parseInt(e.target.value) || 0)}
                          sx={{
                            width: { xs: '60px', sm: '80px' },
                            '& .MuiInputBase-input': {
                              fontSize: { xs: '0.75rem', sm: '0.875rem' }
                            }
                          }}
                        />
                      ) : (
                        image.displayOrder || 0
                      )}
                    </TableCell>

                    {/* Status Column */}
                    <TableCell sx={{ padding: { xs: '8px', sm: '16px' } }}>
                      {isEditing ? (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={editedData.isActive}
                              onChange={(e) => handleInputChange('isActive', e.target.checked)}
                              size="small"
                            />
                          }
                          label={
                            <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                              {editedData.isActive ? "Active" : "Inactive"}
                            </Typography>
                          }
                        />
                      ) : (
                        <Chip
                          label={image.isActive ? "Active" : "Inactive"}
                          size="small"
                          color={image.isActive ? "success" : "default"}
                          variant="outlined"
                          sx={{
                            fontSize: { xs: '0.6rem', sm: '0.7rem', md: '0.75rem' },
                            height: { xs: '20px', sm: '24px' },
                            '& .MuiChip-label': {
                              px: { xs: 0.5, sm: 1 }
                            }
                          }}
                        />
                      )}
                    </TableCell>

                    {/* Date Column */}
                    <TableCell sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      whiteSpace: 'nowrap',
                      padding: { xs: '8px', sm: '16px' }
                    }}>
                      {formatDate(image.createdAt)}
                    </TableCell>

                    {/* Actions Column */}
                    <TableCell sx={{ padding: { xs: '4px', sm: '16px' } }}>
                      <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, flexWrap: 'nowrap' }}>
                        {isEditing ? (
                          <>
                            <Tooltip title="Save changes">
                              <IconButton
                                color="success"
                                onClick={handleSaveEdit}
                                disabled={saveLoading}
                                sx={{ padding: { xs: '4px', sm: '8px' } }}
                              >
                                {saveLoading ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <SaveIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' } }} />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel">
                              <IconButton
                                color="warning"
                                onClick={handleCancelEdit}
                                disabled={saveLoading}
                                sx={{ padding: { xs: '4px', sm: '8px' } }}
                              >
                                <CancelIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' } }} />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <PermissionGuard resource="gallery" action="edit">
                              <Tooltip title="Edit inline">
                                <IconButton
                                  color="primary"
                                  onClick={() => handleStartEdit(image)}
                                  title="Edit Image"
                                  sx={{ padding: { xs: '4px', sm: '8px' } }}
                                >
                                  <EditIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' } }} />
                                </IconButton>
                              </Tooltip>
                            </PermissionGuard>
                            <PermissionGuard resource="gallery" action="delete">
                              <Tooltip title="Delete image">
                                <IconButton
                                  color="error"
                                  onClick={() => handleOpenDialog(image.id)}
                                  title="Delete Image"
                                  sx={{ padding: { xs: '4px', sm: '8px' } }}
                                >
                                  <DeleteIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' } }} />
                                </IconButton>
                              </Tooltip>
                            </PermissionGuard>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog.open}
        onClose={handleCloseDialog}
        onConfirm={handleDelete}
        title="Delete Gallery Image"
        message={`Are you sure you want to delete the image "${deleteDialog.imageTitle}"?`}
        warning="⚠️ This action cannot be undone."
        severity="warning"
        loading={deleteLoading}
        confirmText="Delete Image"
        confirmColor="error"
        fullWidth
        maxWidth="xs"
      />
    </Container>
  );
};

export default GalleryManagement;
